import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Pencil, Trash2, Star, Search, Plus, MoreVertical } from 'lucide-react';
import DeleteIntegrationModal from '../AdobeIoIntegrations/NewIntegration/DeleteIntegrationModal';
import ActivateIntegration from '../AdobeIoIntegrations/NewIntegration/ActivateIntegration';
import DeactivateIntegrationModal from '../AdobeIoIntegrations/NewIntegration/DeactivateIntegrationModal';
import { useUserContext } from '../../contexts/UserContext';
import { updateSpecificAdobeIoProduct } from '../../utils/APIs';
import Loader from '../UI/Loader/Loader';
import DeactivateModal from '../UI/Modal/DeactivateModal';
import ActivationModal from '../UI/Modal/ActivationModal';
import DeleteModal from '../UI/Modal/DeleteModal';
import useComponentVisible from '../UI/ComponentVisible';
import { permission } from '../../config/permission';
import { formatDate } from '../../utils/converter';
import { toast } from 'react-toastify';
import { useContentfulContext } from '../../contexts/ContentfulContext';
import { getAgGridSortStorageKey } from '../UI/AgGrid';
import { Card, Button, Input, AgTableCard } from '../UI';

const IntegrationTable = () => {
    const navigate = useNavigate();
    const { userData, checkPermission, updateAdobeIoIntegrations } = useUserContext();
    const { content } = useContentfulContext();

    const [deactivateIntegration, setDeactivateIntegration] = useState(false);
    const [deleteIntegration, setDeleteIntegration] = useState(false);
    const [activateIntegration, setActivateIntegration] = useState(false);
    const [adobeData, setAdobeData] = useState([]);
    const [loading] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [handleId, setHandleId] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [confirmModal, setConfirmModal] = useState({
        active: false,
        deactivate: false,
        delete: false
    });
    const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
    const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(false);

    useEffect(() => {
        setAdobeData(userData?.adobeIoIntegrations || []);
    }, [userData?.adobeIoIntegrations]);

    const [gridApi, setGridApi] = useState(null);

    const onFilterTextBoxChanged = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        if (gridApi) {
            gridApi.setFilterModel({
                integration_name: val ? { type: 'contains', filter: val.toLowerCase() } : null
            });
        }
    };

    const handleClearSearch = () => {
        setSearchTerm('');
        if (gridApi) {
            gridApi.setFilterModel({});
        }
    };

    const handleSetAsDefault = async (id) => {
        try {
            setActionLoading(true);
            await updateSpecificAdobeIoProduct(
                userData.token,
                userData.selectedOrganization.org_token,
                { productId: id, bodyData: { default: true } }
            );
            updateAdobeIoIntegrations();
            toast.success(content.AdobeIO_CONTENT.success.update);
        } catch (error) {
            toast.error(error.message);
        } finally {
            setActionLoading(false);
        }
    };

    const colDefs = [
        {
            headerName: content.AdobeIO_CONTENT.integration_table_headers.integration_name,
            field: "integration_name",
            cellRenderer: ({ data }) => (
                <button
                    type="button"
                    className="font-medium text-primary hover:text-primary-hover hover:underline text-left"
                    onClick={() => navigate(`/setup/integration/edit/${data._id}`)}
                >
                    {data.integration_name}
                </button>
            ),
            filter: true,
            sortable: true,
            suppressMovable: true,
        },
        {
            headerName: content.AdobeIO_CONTENT.integration_table_headers.associated_products,
            field: "products",
            cellRenderer: ({ data }) => {
                const products = data.products || [];
                if (products.length === 0) return <span className="text-stride-muted">—</span>;
                return (
                    <div className="flex flex-wrap gap-1.5 py-1">
                        {products.map((p, i) => (
                            <span key={i} className="stride-badge-pill bg-slate-100 text-slate-700 text-xs font-medium">
                                {p.name}
                            </span>
                        ))}
                    </div>
                );
            },
            suppressMovable: true,
        },
        {
            headerName: content.AdobeIO_CONTENT.integration_table_headers.default,
            field: "default",
            sortable: false,
            cellRenderer: ({ data }) => {
                const isDefault = data.default === true || data.default === "true";
                return isDefault ? (
                    <span className="stride-badge-pill bg-emerald-50 text-emerald-700 font-semibold text-xs">
                        Default
                    </span>
                ) : (
                    <span className="text-stride-muted text-xs">—</span>
                );
            },
            suppressMovable: true
        },
        {
            headerName: content.AdobeIO_CONTENT.integration_table_headers.updated_at,
            field: "updatedAt",
            cellRenderer: ({ data }) => (
                <span className="text-stride-muted text-xs">
                    {formatDate(data.updatedAt)}
                </span>
            ),
            comparator: (date1, date2) => new Date(date1) - new Date(date2),
            sortable: true,
            suppressMovable: true
        },
        {
            headerName: 'Actions',
            field: 'actions',
            width: 140,
            sortable: false,
            filter: false,
            resizable: false,
            suppressMovable: true,
            cellRenderer: ({ data }) => {
                const canEdit = checkPermission(permission.EDIT_ADOBE_IO);
                const canDelete = checkPermission(permission.DELETE_ADOBE_IO);
                const canSetDefault = checkPermission(permission.EDIT_ADOBE_IO_OP_EDIT_DEFAULT);
                const isDefault = data.default === true || data.default === "true";

                return (
                    <div className="stride-table-actions">
                        {canEdit && (
                            <button
                                type="button"
                                title="Edit Integration"
                                className="stride-table-btn stride-table-btn-edit"
                                onClick={() => navigate(`/setup/integration/edit/${data._id}`)}
                            >
                                <Pencil className="w-3.5 h-3.5" />
                            </button>
                        )}
                        {canSetDefault && !isDefault && (
                            <button
                                type="button"
                                title="Set as default"
                                className="stride-table-btn hover:text-amber-600 hover:border-amber-300"
                                onClick={() => handleSetAsDefault(data._id)}
                            >
                                <Star className="w-3.5 h-3.5 text-amber-500" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                type="button"
                                title="Delete Integration"
                                className="stride-table-btn stride-table-btn-delete"
                                onClick={() => {
                                    setHandleId(data._id);
                                    setDeleteIntegration(true);
                                }}
                            >
                                <Trash2 className="w-3.5 h-3.5" />
                            </button>
                        )}
                        <button
                            type="button"
                            className="stride-table-btn actions"
                            title="More options"
                            onClick={(e) => {
                                e.stopPropagation();
                                setHandleId(data._id);
                                setIsComponentVisible(!isComponentVisible);
                                const buttonRect = e.currentTarget.getBoundingClientRect();
                                setMenuPosition({
                                    top: buttonRect.bottom + window.scrollY,
                                    left: buttonRect.left + window.scrollX
                                });
                            }}
                        >
                            <MoreVertical className="w-3.5 h-3.5" />
                        </button>
                    </div>
                );
            },
        },
    ];

    const onGridReady = (params) => {
        setGridApi(params.api);

        const storageKey = getAgGridSortStorageKey("adobe-io-integration-table");
        const sortModel = JSON.parse(localStorage.getItem(storageKey));

        if (!sortModel || sortModel?.length === 0) {
            params.columnApi.applyColumnState({
                state: [
                    { colId: 'updatedAt', sort: 'desc' }
                ],
                applyOrder: false,
            });
        }
    };

    const autoSizeStrategy = useMemo(() => {
        return {
            type: "fitGridWidth",
            defaultMinWidth: 40,
            columnLimits: [
                {
                    colId: "integration_name",
                    minWidth: 260,
                },
                {
                    colId: "products",
                    minWidth: 280,
                },
                {
                    colId: "default",
                    minWidth: 120,
                },
                {
                    colId: "updatedAt",
                    minWidth: 160,
                },
                {
                    colId: "actions",
                    minWidth: 140,
                }
            ],
        };
    }, []);

    return (
        <div className="flex flex-col space-y-6">
            {/* Empty State */}
            {!loading && (!adobeData || adobeData.length === 0) ? (
                <Card className="text-center p-12 max-w-lg mx-auto">
                    <p className="text-base text-stride-heading font-medium mb-4">
                        {content.AdobeIO_CONTENT.error.FE133}
                    </p>
                    {checkPermission(permission.CREATE_ADOBE_IO) && (
                        <Button
                            variant="primary"
                            icon={<Plus className="w-4 h-4" />}
                            onClick={() => navigate('/setup/new-integration')}
                        >
                            {content.AdobeIO_CONTENT.new_integration_cta_text}
                        </Button>
                    )}
                </Card>
            ) : (
                <AgTableCard
                    header={
                        <>
                            <div className="w-full md:w-80">
                                <Input
                                    placeholder="Search integration name..."
                                    value={searchTerm}
                                    onChange={onFilterTextBoxChanged}
                                    prefixIcon={<Search className="w-4 h-4 text-stride-muted" />}
                                    clearable
                                    onClear={handleClearSearch}
                                />
                            </div>

                            {checkPermission(permission.CREATE_ADOBE_IO) && (
                                <Button
                                    id="create-adobeIo-cta"
                                    variant="primary"
                                    icon={<Plus className="w-4 h-4" />}
                                    onClick={() => navigate('/setup/new-integration')}
                                >
                                    {content.AdobeIO_CONTENT.new_integration_cta_text}
                                </Button>
                            )}
                        </>
                    }
                    tableId="adobe-io-integration-table"
                    loading={loading}
                    rowData={adobeData}
                    columnDefs={colDefs}
                    onGridReady={onGridReady}
                    autoSizeStrategy={autoSizeStrategy}
                >
                    {isComponentVisible && (
                        <div
                            ref={ref}
                            className="absolute z-10 mt-2 w-48 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none py-1"
                            style={{ top: menuPosition.top, left: Math.max(10, menuPosition.left - 180) }}
                        >
                            <div className="py-1">
                                {checkPermission(permission.EDIT_ADOBE_IO) && (
                                    <a
                                        href="#"
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                        tabIndex="-1"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsComponentVisible(false);
                                            navigate(`/setup/integration/edit/${handleId}`);
                                        }}
                                    >
                                        {content.AdobeIO_CONTENT.more_info_options.edit}
                                    </a>
                                )}
                                {checkPermission(permission.DELETE_ADOBE_IO) && (
                                    <a
                                        href="#"
                                        className="text-rose-600 block px-4 py-2 text-sm hover:bg-gray-100"
                                        tabIndex="-1"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setIsComponentVisible(false);
                                            setDeleteIntegration(true);
                                        }}
                                    >
                                        {content.AdobeIO_CONTENT.more_info_options.delete}
                                    </a>
                                )}
                                {checkPermission(permission.EDIT_ADOBE_IO_OP_EDIT_DEFAULT) && (
                                    <a
                                        href="#"
                                        className="text-gray-700 block px-4 py-2 text-sm hover:bg-gray-100"
                                        tabIndex="-1"
                                        onClick={async (e) => {
                                            e.preventDefault();
                                            setIsComponentVisible(false);
                                            await handleSetAsDefault(handleId);
                                        }}
                                    >
                                        {content.AdobeIO_CONTENT.more_info_options.set_as_default}
                                    </a>
                                )}
                            </div>
                        </div>
                    )}
                </AgTableCard>
            )}

            {actionLoading && <Loader />}

            {deactivateIntegration && (
                <DeactivateIntegrationModal
                    setConfirmModal={setConfirmModal}
                    id={handleId}
                    setDeactivateIntegration={setDeactivateIntegration}
                    setActionLoading={setActionLoading}
                    getAdobeData={updateAdobeIoIntegrations}
                />
            )}
            {deleteIntegration && (
                <DeleteIntegrationModal
                    setConfirmModal={setConfirmModal}
                    id={handleId}
                    setDeleteIntegration={setDeleteIntegration}
                    setActionLoading={setActionLoading}
                    getAdobeData={updateAdobeIoIntegrations}
                />
            )}
            {activateIntegration && (
                <ActivateIntegration
                    setConfirmModal={setConfirmModal}
                    id={handleId}
                    setActivateIntegration={setActivateIntegration}
                    setActionLoading={setActionLoading}
                    getAdobeData={updateAdobeIoIntegrations}
                />
            )}
            {!loading && confirmModal?.deactivate && (
                <DeactivateModal
                    setConfirmModal={setConfirmModal}
                    setDeactivateIntegration={setDeactivateIntegration}
                />
            )}
            {!loading && confirmModal?.active && (
                <ActivationModal
                    setConfirmModal={setConfirmModal}
                    setActivateIntegration={setActivateIntegration}
                />
            )}
            {!loading && confirmModal?.delete && (
                <DeleteModal
                    setConfirmModal={setConfirmModal}
                    setDeleteIntegration={setDeleteIntegration}
                />
            )}
        </div>
    );
};

export default IntegrationTable;
